// Central catalog of backend endpoint URL builders, exposed as $api.routes.
// All HTTP transfer goes through usePbApi (composables/api.ts) — the single fetch path.
export default defineNuxtPlugin(() => {
    return {
        provide: {
            api: {
                routes: {
                    me: {
                        getMe: () => `/me`,
                        emails: {
                            sendVerification: () => `/me/emails/verify`,
                        },

                        mfa: {
                            get: () => `/me/mfa`,
                            enable: () => `/me/mfa`,
                            disable: () => `/me/mfa`,
                        },
                        passkeys: {
                            get: () => `/me/passkeys`,
                            delete: (id: number) => `/me/passkeys/${id}`,
                        },
                        loginActivity: {
                            get: () => `/me/login-activity`,
                            revoke: (tokenId: string) => `/me/login-activity/${tokenId}`,
                        },

                        financial: {
                            confirmPayment: () => `/me/financial/confirm-payment`,
                        },
                    },
                    companies: {
                        deletion_guide: (company_id: number): string => `public/companies/${company_id}/guide`,
                        faqs: (company_id: number): string => `public/companies/${company_id}/faqs`,
                        risk_factors: (company_id: number): string => `public/companies/${company_id}/risk-factors`,
                        advisories: (company_id: number): string => `public/companies/${company_id}/advisories`,
                    },
                    people: {
                        person: (person_id: number) => `/people/${person_id}`,
                        billing: {
                            creditBalance: (person_id: number) => `/people/${person_id}/financial/credit-balance`,
                            giftCertificates: {
                                purchase: (person_id: number) => `/people/${person_id}/financial/gift-certificates`,
                                apply: (person_id: number) => `/people/${person_id}/financial/gift-certificates/apply`,
                                email: (person_id: number, code: string) =>
                                    `/people/${person_id}/financial/gift-certificates/${code}/email`,
                            },
                            invoices: {
                                get: (person_id: number, page: number = 1, perPage: number = 10) =>
                                    `/people/${person_id}/financial/invoices?page=${page}&per_page=${perPage}`,
                                download: (person_id: number, invoice_id: string) =>
                                    `/people/${person_id}/financial/invoices/${invoice_id}`,
                            },
                        },
                        exposures: {
                            exposure: (person_id: number, exposure_id: string) => `/people/${person_id}/exposures/${exposure_id}`,
                            exposure_history: (person_id: number, exposure_id: string) =>
                                `/people/${person_id}/exposures/${exposure_id}/history`,
                            company_history: (person_id: number, company_id: number) =>
                                `/people/${person_id}/exposures/company/${company_id}/history`,
                            pipeline: (person_id: number) => `/people/${person_id}/exposures/statistics/pipeline`,
                            statistics: (person_id: number) => `/people/${person_id}/exposures/statistics`,
                            digital_footprint: (person_id: number, months: number = 6) =>
                                `/people/${person_id}/exposures/statistics/digital-footprint?months=${months}`,
                            recent: (person_id: number, limit: number = 6) =>
                                `/people/${person_id}/exposures/recent?limit=${limit}`,
                            data_exposed: (person_id: number) => `/people/${person_id}/exposures/data-exposed/v2`,
                            detail: (person_id: number) => `/people/${person_id}/exposures/detail`,
                            list: (person_id: number, limit: number = 50) => `/people/${person_id}/exposures?limit=${limit}`,
                            history_export: (person_id: number) => `/people/${person_id}/exposures/history/export`,
                            radar: (person_id: number) => `/people/${person_id}/exposures/radar`,
                            rapid_response: {
                                store: (person_id: number) => `/people/${person_id}/exposures/rapid-response`,
                            },
                            suppressions: {
                                get_suppressions: (person_id: number) => `/people/${person_id}/exposures/suppressions`,
                                update_suppressions: (person_id: number) => `/people/${person_id}/exposures/suppressions`,
                                remove_suppression: (person_id: number, suppressionId: number) =>
                                    `/people/${person_id}/exposures/suppressions/${suppressionId}`,
                            },
                            custom_removal: (person_id: number) => `/people/${person_id}/exposures/custom-removal`,
                        },
                        breaches: {
                            list: (person_id: number) => `/people/${person_id}/breaches`,
                            scan: (person_id: number) => `/people/${person_id}/breaches/scan`,
                        },
                        companies: {
                            get_companies: (person_id: number) => `/people/${person_id}/companies`,
                            get_company: (company_id: number) => `/companies/${company_id}`,
                            search: (query: string, per_page: number, page: number, type: string) =>
                                `/companies?per_page=${per_page}&page=${page}&search=${query}&type=${type}`,
                            trust_company: (person_id: number, company_id: number) =>
                                `/people/${person_id}/companies/${company_id}/trust`,
                            untrust_company: (person_id: number, company_id: number) =>
                                `/people/${person_id}/companies/${company_id}/untrust`,
                            scan_company: (person_id: number, company_id: number) =>
                                `/people/${person_id}/companies/${company_id}/scan`,
                            conversation: (person_id: number, company_id: number) =>
                                `/people/${person_id}/companies/${company_id}/conversation`,
                            channels: (person_id: number, company_id: number) =>
                                `/people/${person_id}/companies/${company_id}/channels`,
                            updateChannel: (person_id: number, company_id: number, channel_id: number) =>
                                `/people/${person_id}/companies/${company_id}/channels/${channel_id}`,
                            exposures: (person_id: number, company_id: number) =>
                                `/people/${person_id}/companies/${company_id}/exposures`,
                        },

                        searchResults: {
                            /**
                             * GET /v3/people/{person_id}/search-results
                             * @param person_id - The person ID
                             * @param status - Filter by status: 'active' | 'removed' | 'ignored'
                             * @param after_key - The "after key" AKA page identifier
                             */
                            list: (person_id: number, status?: string, after_key?: string | null) => {
                                const params = new URLSearchParams(
                                    Object.entries({ status, after: after_key }).filter((e): e is [string, string] => !!e[1])
                                ).toString();

                                return `/people/${person_id}/search-results${params ? `?${params}` : ''}`;
                            },
                            /**
                             * GET /v3/people/{person_id}/search-results/statistics
                             * Get statistics about search results
                             */
                            statistics: (person_id: number) => `/people/${person_id}/search-results/statistics`,
                            /**
                             * PUT /v3/people/{person_id}/search-results/{urlHash}
                             * @param person_id - The person ID
                             * @param urlHash - The URL hash of the search result
                             */
                            update: (person_id: number, urlHash: string) => `/people/${person_id}/search-results/${urlHash}`,
                            /**
                             * POST /v3/people/{person_id}/search-results/scan
                             * Trigger a new scan (runs as background job)
                             */
                            scan: (person_id: number) => `/people/${person_id}/search-results/scan`,
                            /**
                             * GET /v3/people/{person_id}/search-results/scan/status
                             * Get the current scan status
                             */
                            scanStatus: (person_id: number) => `/people/${person_id}/search-results/scan/status`,
                            /**
                             * POST /v3/people/{person_id}/search-results/mark-as-exposure
                             * Create an exposure from a search result
                             */
                            markAsExposure: (person_id: number) => `/people/${person_id}/search-results/mark-as-exposure`,
                        },

                        contacts: {
                            get_contacts: (person_id: number) => `/people/${person_id}/contacts?per_page=10000&blocked=false`,
                            create_contact: (person_id: number) => `/people/${person_id}/contacts`,
                            delete: (person_id: number, contact_id: number) => `/people/${person_id}/contacts/${contact_id}`,
                            minor_eligibility: (person_id: number) => `/people/${person_id}/contacts/minor-eligibility`,
                            sign_minor_consent: (person_id: number) => `/people/${person_id}/contacts/minor-consent`,
                            create_minor: (person_id: number) => `/people/${person_id}/contacts/minor`,
                        },

                        //pb university
                        university: {
                            get_statistics: (person_id: number) => `/people/${person_id}/university/statistics`,
                            get_module_info: (person_id: number, module: string) =>
                                `/people/${person_id}/university/modules/${module}`,
                            get_lesson_info: (person_id: number, module: string, lesson_id: number) =>
                                `/people/${person_id}/university/modules/${module}/lesson/${lesson_id}`,
                            update_lesson_progress: (person_id: number, module: string, lesson_id: number) =>
                                `/people/${person_id}/university/modules/${module}/lesson/${lesson_id}`,
                        },

                        licenses: {
                            purchase_licenses: (person_id: number) => `/people/${person_id}/licenses`,
                            update_license: (person_id: number, license_id: number) =>
                                `/people/${person_id}/licenses/${license_id}`,
                            change_license: (person_id: number, license_id: number) =>
                                `/people/${person_id}/licenses/${license_id}/change`,
                            refund: (person_id: number, license_id: number) => `/people/${person_id}/licenses/${license_id}`,
                        },
                        akas: {
                            create: (person: number) => `/people/${person}/akas`,
                            update: (person: number, akaId: number) => `/people/${person}/akas/${akaId}`,
                            delete: (person: number, akaId: number) => `/people/${person}/akas/${akaId}`,
                        },
                        emails: {
                            sendVerification: () => `/me/emails/verify`,
                            create: (person: number) => `/people/${person}/emails`,
                            delete: (person: number, email: number) => `/people/${person}/emails/${email}`,
                            update: (person: number, email: number) => `/people/${person}/emails/${email}`,
                        },
                        address: {
                            create: (person: number) => `/people/${person}/addresses`,
                            delete: (person: number, address: number) => `/people/${person}/addresses/${address}`,
                            update: (person: number, address: number) => `/people/${person}/addresses/${address}`,
                        },
                        phones: {
                            create: (person: number) => `/people/${person}/phones`,
                            delete: (person: number, phone: number) => `/people/${person}/phones/${phone}`,
                            sendVerification: (person: number) => `/people/${person}/phones/verify`,
                            update: (person: number, phone: number) => `/people/${person}/phones/${phone}`,
                        },
                        options: {
                            get: (person: number) => `/people/${person}/options`,
                            update: (person: number) => `/people/${person}/options`,
                        },
                        communicationPreferences: {
                            get: (person: number) => `/people/${person}/communication-preferences`,
                            update: (person: number) => `/people/${person}/communication-preferences`,
                        },
                        poa: {
                            sign: (person: number) => `/people/${person}/lpoa`,
                            retract: (person: number) => `/people/${person}/lpoa`,
                        },
                    },
                    public: {
                        companies: {
                            search: (
                                query: string,
                                per_page: number,
                                page: number,
                                type: string,
                                trust: boolean,
                                license?: string
                            ) => {
                                let url = `/public/companies?per_page=${per_page}&page=${page}&search=${query}&type=${type}&trust_page=${trust}`;
                                if (license) {
                                    url += `&license=${license}`;
                                }
                                return url;
                            },
                            data_broker: (query: string, per_page: number, page: number, trust: boolean) =>
                                `/public/companies/data_broker?per_page=${per_page}&search=${query}&page=${page}&trust_page=${trust}`,
                            stats: () => `/public/companies/stats`,
                            lookup: (slug: string) => `/public/companies/lookup/${slug}?guide=true`,
                            advisories: (company_id: number) => `/public/companies/${company_id}/advisories`,
                            faqs: (company_id: number) => `/public/companies/${company_id}/faqs`,
                            risk_factors: (company_id: number) => `/public/companies/${company_id}/risk-factors`,
                            deletion_guide: (company_id: number) => `/public/companies/${company_id}/guide`,
                        },
                    },
                    countries: {
                        lookup: (id: number) => `/countries/${id}`,
                    },
                    promo_codes: {
                        validate: (code: string, license: string, term: number) =>
                            `/promo-codes/validate?code=${encodeURIComponent(code)}&license=${license}&term=${term}`,
                    },
                    auth: {
                        passkeys: {
                            registerOptions: () => `/auth/passkeys/register-options`,
                            register: () => `/auth/passkeys/register`,
                        },
                    },
                },
            },
        },
    };
});
